"use client";
/**
 * SCURTĂTURILE DE TASTATURĂ ale spațiului de lucru, într-un singur loc, plus ecranul care
 * le arată (tasta `?`).
 *
 * De ce într-un singur loc: o scurtătură împrăștiată prin ecrane devine o scurtătură care
 * există pe patru pagini din douăzeci, iar omul nu se poate baza pe ea. Aici e lista, aici
 * se ascultă tastele, aici se și afișează — nu pot diverge.
 *
 * ⛔ Regula care face diferența între ajutor și sabotaj: **cât timp omul scrie, tastele îi
 * aparțin lui**. `?` într-un câmp de text e semnul întrebării, nu un ecran de ajutor; `/`
 * într-o denumire de produs e o bară oblică. De aceea fiecare scurtătură fără modificator
 * se oprește când focusul e într-un `input`, `textarea`, `select` sau într-o zonă
 * `contenteditable`. Cele cu Ctrl trec, fiindcă nu se pot tasta din greșeală.
 */
import { useEffect, useState } from "react";
import { deschidePaleta } from "@/components/command-palette";

/** Lista, ca DATE — ecranul de ajutor o randează, nu o rescrie. */
export const SCURTATURI: { taste: string[]; ce: string; unde: string }[] = [
  { taste: ["Ctrl", "K"], ce: "Deschide paleta: acțiuni, pagini, clienți, produse, facturi", unde: "oriunde" },
  { taste: ["/"], ce: "Același lucru, dintr-o singură tastă", unde: "oriunde" },
  { taste: ["?"], ce: "Arată lista asta", unde: "oriunde" },
  { taste: ["sus", "jos"], ce: "Plimbă alegerea prin rezultate", unde: "în paletă și în căutarea de articole" },
  { taste: ["Enter"], ce: "Deschide ce e ales", unde: "în paletă" },
  { taste: ["Enter"], ce: "Ia articolul evidențiat și sare la cantitate", unde: "în căutarea de articole" },
  { taste: ["Enter"], ce: "Trece la linia următoare; pe ultima, o creează", unde: "în cantitate și în preț" },
  { taste: ["Alt", "N"], ce: "Adaugă o linie nouă", unde: "în documentele cu linii" },
  { taste: ["Ctrl", "Enter"], ce: "Salvează documentul deschis", unde: "în dialoguri" },
  { taste: ["Ctrl", "S"], ce: "Același lucru", unde: "în dialoguri" },
  { taste: ["Esc"], ce: "Închide dialogul, panoul sau lista deschisă", unde: "oriunde" },
];

/** Scrie omul ACUM? Atunci tastele simple sunt ale lui, nu ale aplicației. */
export function seScrieAcum(): boolean {
  const el = document.activeElement as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el.isContentEditable;
}

export function Scurtaturi() {
  const [ajutor, setAjutor] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && ajutor) { e.preventDefault(); setAjutor(false); return; }
      /* Cu un modificator apăsat, tasta e a altcuiva (Ctrl+/ e o scurtătură de browser). */
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (seScrieAcum()) return;
      if (e.key === "?") { e.preventDefault(); setAjutor((v) => !v); return; }
      if (e.key === "/") { e.preventDefault(); deschidePaleta(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [ajutor]);

  if (!ajutor) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" role="dialog" aria-modal="true" aria-label="Scurtături de tastatură">
      <button aria-label="Închide" tabIndex={-1} onClick={() => setAjutor(false)} className="absolute inset-0 cursor-default bg-ink/25 backdrop-blur-[2px]" />
      <div className="relative w-full max-w-[560px] overflow-hidden rounded-[var(--l-r-pop)] border border-line bg-surface shadow-pop">
        <div className="flex items-center justify-between border-b border-line px-5 py-3.5">
          <p className="font-display text-[17px] font-medium text-ink">Scurtături de tastatură</p>
          <kbd className="rounded border border-line px-1.5 py-0.5 font-mono text-[10px] text-ink-3">ESC</kbd>
        </div>
        <div className="max-h-[62vh] overflow-y-auto py-1">
          {SCURTATURI.map((s, i) => (
            <div key={i} className="flex items-baseline gap-3 px-5 py-2">
              <span className="flex shrink-0 gap-1">
                {s.taste.map((t) => (
                  <kbd key={t} className="rounded border border-line bg-surface-2 px-1.5 py-0.5 font-mono text-[11px] text-ink-2">{t}</kbd>
                ))}
              </span>
              <span className="min-w-0 flex-1 text-[13px] text-ink">{s.ce}</span>
              <span className="shrink-0 text-[11.5px] text-ink-3">{s.unde}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
